import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { getFixedAssets, createFixedAsset } from '../../../api/erp';
import { toast } from 'react-toastify';
import BlurredValue from '../../../components/common/BlurredValue';

const WholesaleFixedAssetsPage = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '', asset_code: '', purchase_date: '', purchase_cost: '', useful_life_years: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await getFixedAssets({ module: 'wholesale' });
      setAssets(data);
    } catch { toast.error('Failed to load wholesale assets'); } 
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createFixedAsset({ ...formData, module: 'wholesale' });
      toast.success('Asset registered successfully');
      setShowModal(false);
      fetchData();
    } catch { toast.error('Failed to create wholesale asset'); }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Wholesale Fixed Assets</h1>
        <button 
          onClick={() => setShowModal(true)} 
          className="flex items-center gap-2 text-white px-4 py-2 rounded transition-colors"
          style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
        >
          <Plus size={16} /> Register Asset
        </button>
      </div>

      <div className="bg-white rounded shadow overflow-hidden border border-gray-100">
        <table className="w-full">
          <thead className="bg-gray-50 text-gray-700 uppercase text-xs">
            <tr>
              <th className="px-6 py-3 text-left">Asset Code</th>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Purchase Date</th>
              <th className="px-6 py-3 text-right">Cost</th>
              <th className="px-6 py-3 text-right">Book Value</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {assets.length > 0 ? (
              assets.map(a => (
                <tr key={a.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-6 py-4 font-mono text-emerald-700">{a.asset_code}</td>
                  <td className="px-6 py-4 font-medium text-gray-900">{a.name}</td>
                  <td className="px-6 py-4 text-gray-600">{new Date(a.purchase_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right"><BlurredValue value={a.purchase_cost} /></td>
                  <td className="px-6 py-4 text-right font-semibold text-gray-900"><BlurredValue value={a.current_book_value} /></td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  {loading ? 'Loading assets...' : 'No wholesale assets found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md border border-gray-100">
            <h2 className="text-xl font-bold mb-4 text-gray-900">Register Wholesale Asset</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asset Code *</label>
                <input 
                  placeholder="e.g. WS-LPT-001" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  onChange={e => setFormData({...formData, asset_code: e.target.value})} 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name *</label>
                <input 
                  placeholder="e.g. Delivery Truck" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  onChange={e => setFormData({...formData, name: e.target.value})} 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date *</label>
                <input 
                  type="date" 
                  className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  onChange={e => setFormData({...formData, purchase_date: e.target.value})} 
                  required 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost (₦) *</label>
                  <input 
                    type="number" 
                    placeholder="0.00" 
                    className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                    onChange={e => setFormData({...formData, purchase_cost: e.target.value})} 
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Life (Years) *</label>
                  <input 
                    type="number" 
                    placeholder="e.g. 5" 
                    className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                    onChange={e => setFormData({...formData, useful_life_years: e.target.value})} 
                    required 
                  />
                </div>
              </div>
              
              <div className="flex gap-2 justify-end pt-4 border-t border-gray-100">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-4 py-2 text-white rounded transition-colors"
                  style={{ backgroundColor: 'var(--wholesale-primary, #047857)' }}
                >
                  Register
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesaleFixedAssetsPage;
